import React from 'react';
import { Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

export function Hero({ storefrontSettings, settingsLoading, onScrollTo }) {
    const [heroRef, heroRevealed] = useScrollReveal({ threshold: 0.2 });
    const [imageRef, imageRevealed] = useScrollReveal({ threshold: 0.2 });

    const handleScroll = (id) => {
        onScrollTo?.(id);
    };

    return (
        <header className="relative bg-[#fdfbf7] overflow-hidden">
            {/* Decorative Background Blobs */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#fae1e5] rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse"></div>
            <div className="absolute -bottom-24 left-0 w-72 h-72 bg-[#f3e6d5] rounded-full mix-blend-multiply filter blur-3xl opacity-70 float"></div>

            <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-32 flex flex-col md:flex-row items-center">
                <div ref={heroRef} className={`md:w-1/2 md:pr-12 ${heroRevealed ? 'reveal revealed' : 'reveal'}`}>
                    {storefrontSettings?.heroBadge && (
                        <div className="inline-flex items-center px-3 py-1 rounded-full border border-[#c5a059] text-[#c5a059] text-xs font-semibold tracking-wider mb-6 uppercase hover-glow">
                            <Sparkles size={14} className="mr-2 animate-pulse-slow" />
                            {storefrontSettings.heroBadge}
                        </div>
                    )}
                    <h1 className="text-5xl md:text-6xl font-serif text-gray-800 leading-tight mb-6 animate-fadeIn">
                        {storefrontSettings?.heroTagline || 'Return to your'} <span className="text-[#d48c95] italic gradient-text">{storefrontSettings?.heroTaglineHighlight || 'truest self.'}</span>
                    </h1>
                    {storefrontSettings?.heroDescription && (
                        <p className="text-lg text-gray-600 mb-8 leading-relaxed animate-slideUp">
                            {storefrontSettings.heroDescription}
                        </p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 stagger-children">
                        <button
                            onClick={() => handleScroll('programme')}
                            className="w-full sm:w-auto px-8 py-4 sm:py-3.5 bg-[#c5a059] text-white rounded-full font-medium shadow-lg hover:bg-[#b08e4d] hover:shadow-xl transition-all duration-300 flex items-center justify-center min-h-[48px] text-base sm:text-sm hover-lift button-press focus-ring group"
                        >
                            <span className="flex items-center">
                                View The Programme
                                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>
                        <button
                            onClick={() => handleScroll('freebie')}
                            className="w-full sm:w-auto px-8 py-4 sm:py-3.5 border-2 border-[#d48c95] text-[#d48c95] rounded-full font-medium hover:bg-[#fae1e5] hover:border-[#c07b83] transition-all duration-300 flex items-center justify-center min-h-[48px] text-base sm:text-sm hover-lift button-press focus-ring"
                        >
                            Get Free Meditation
                        </button>
                    </div>
                </div>

                {/* Profile Picture */}
                <div ref={imageRef} className={`md:w-1/2 mt-12 md:mt-0 relative ${imageRevealed ? 'reveal revealed' : 'reveal'}`}>
                    <div className="relative z-10 rounded-t-full rounded-b-3xl overflow-hidden border-8 border-white shadow-2xl mx-auto w-full max-w-md h-[500px] bg-gray-200 hover-scale transition-transform duration-500">
                        {settingsLoading ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                <Loader2 className="animate-spin text-[#c5a059]" size={32} />
                            </div>
                        ) : storefrontSettings?.profilePicture ? (
                            <img
                                src={storefrontSettings.profilePicture}
                                alt={storefrontSettings.displayName || "Fabiha Zaman"}
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                loading="eager"
                                onError={(e) => {
                                    // Fallback if image fails to load
                                    e.target.src = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=1000&auto=format&fit=crop";
                                }}
                            />
                        ) : (
                            <img
                                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=1000&auto=format&fit=crop"
                                alt="Fabiha Zaman"
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                            />
                        )}
                    </div>
                    {/* Decorative Gold Circle behind */}
                    <div className="absolute top-10 right-10 w-full h-full border-2 border-[#c5a059] rounded-t-full rounded-b-3xl -z-0 translate-x-4 translate-y-4 opacity-50"></div>
                </div>
            </div>
        </header>
    );
}

export default Hero;
